import {
  loadAttachments,
  saveAttachments,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatAttachmentStore'
import {
  type DocChatPendingAttachment,
  MAX_DOC_CHAT_ATTACHMENTS,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatImageAttachments'
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

interface DocChatAttachmentsContextType {
  /** Images staged on the composer, not yet sent. */
  pendingAttachments: DocChatPendingAttachment[]
  addAttachments: (attachments: DocChatPendingAttachment[]) => void
  removeAttachment: (id: string) => void
  clearPendingAttachments: () => void
  /** Images that went out with the in-flight DocChat turn; these ride the request. */
  submittedAttachments: DocChatPendingAttachment[]
  /** Every image sent this session, by attachment id, for transcript scrollback. */
  sentAttachmentsById: Map<string, DocChatPendingAttachment>
  retainSentAttachments: (attachments: DocChatPendingAttachment[]) => void
  /** Rehydrate attachments a restored transcript references but memory lost. */
  requestAttachments: (ids: readonly string[]) => void
  /** Hand the staged images to the turn being submitted and clear the composer. */
  commitPendingAttachments: () => void
}

const DocChatAttachmentsContext = createContext<
  DocChatAttachmentsContextType | undefined
>(undefined)

/**
 * Holds DocChat image attachments outside the persisted search state, which is
 * written to session storage and must not carry megabytes of base64.
 */
export function DocChatAttachmentsProvider({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement {
  const [pendingAttachments, setPendingAttachments] = useState<
    DocChatPendingAttachment[]
  >([])
  const [submittedAttachments, setSubmittedAttachments] = useState<
    DocChatPendingAttachment[]
  >([])
  // Kept for the whole session, not just the in-flight turn, so scrolling back
  // to an earlier turn still shows what was sent with it.
  const [sentAttachmentsById, setSentAttachmentsById] = useState<
    Map<string, DocChatPendingAttachment>
  >(new Map())

  const addAttachments = useCallback(
    (attachments: DocChatPendingAttachment[]) => {
      // Capped here, not only in the composer: concurrent attach flows each
      // see room before either lands, and the backend rejects over-cap turns.
      setPendingAttachments((current) =>
        [...current, ...attachments].slice(0, MAX_DOC_CHAT_ATTACHMENTS)
      )
    },
    []
  )

  const removeAttachment = useCallback((id: string) => {
    setPendingAttachments((current) =>
      current.filter((attachment) => attachment.id !== id)
    )
  }, [])

  const clearPendingAttachments = useCallback(
    () => setPendingAttachments([]),
    []
  )

  const retainSentAttachments = useCallback(
    (attachments: DocChatPendingAttachment[]) => {
      if (!attachments.length) return
      setSentAttachmentsById((sent) => {
        const next = new Map(sent)
        attachments.forEach((attachment) => next.set(attachment.id, attachment))
        return next
      })
    },
    []
  )

  const commitPendingAttachments = useCallback(() => {
    setPendingAttachments((current) => {
      setSubmittedAttachments(current)
      if (current.length > 0) {
        retainSentAttachments(current)
        // The chat tab can unmount; the bytes have to outlive it.
        void saveAttachments(current)
      }
      return []
    })
  }, [retainSentAttachments])

  // Ids already asked for, so a transcript re-render cannot loop on a miss.
  const requestedIdsRef = useRef<Set<string>>(new Set())
  const requestAttachments = useCallback(
    (ids: readonly string[]) => {
      const wanted = ids.filter((id) => !requestedIdsRef.current.has(id))
      if (!wanted.length) return
      wanted.forEach((id) => requestedIdsRef.current.add(id))
      void loadAttachments(wanted).then(retainSentAttachments)
    },
    [retainSentAttachments]
  )

  const value = useMemo<DocChatAttachmentsContextType>(
    () => ({
      pendingAttachments,
      addAttachments,
      removeAttachment,
      clearPendingAttachments,
      submittedAttachments,
      sentAttachmentsById,
      retainSentAttachments,
      requestAttachments,
      commitPendingAttachments,
    }),
    [
      pendingAttachments,
      addAttachments,
      removeAttachment,
      clearPendingAttachments,
      submittedAttachments,
      sentAttachmentsById,
      retainSentAttachments,
      requestAttachments,
      commitPendingAttachments,
    ]
  )

  return (
    <DocChatAttachmentsContext.Provider value={value}>
      {children}
    </DocChatAttachmentsContext.Provider>
  )
}

export function useDocChatAttachments(): DocChatAttachmentsContextType {
  const ctx = useContext(DocChatAttachmentsContext)
  if (!ctx) {
    throw new Error(
      'useDocChatAttachments must be used within a DocChatAttachmentsProvider'
    )
  }
  return ctx
}
